<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { formatDate, formatMoney, isZeroMoney, moneyProportion, type Money, zeroMoney } from '@taler/domain'
import { computed, onBeforeUnmount, onMounted, ref, useId, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  nearestTrendIndex,
  trendFractions,
  trendTicks,
  type TrendPoint,
} from '../internal/net-worth-trend.ts'

const props = defineProps<{ points: readonly TrendPoint[]; resolutionDays: number }>()
const { t, locale } = useI18n()
const localeCode = computed(() => locale.value as 'en' | 'hu')

const VIEW_WIDTH = 960
const VIEW_HEIGHT = 300
const MIN_PAD_LEFT = 84
const LABEL_CHAR_WIDTH = 7.5
const PAD_RIGHT = 12
const PAD_TOP = 16
const PAD_BOTTOM = 32
const PLOT_HEIGHT = VIEW_HEIGHT - PAD_TOP - PAD_BOTTOM
const BOTTOM = VIEW_HEIGHT - PAD_BOTTOM
const X_TICK_FRACTIONS = [0, 0.25, 0.5, 0.75, 1] as const

const plot = useTemplateRef<HTMLElement>('plot')
const hovered = ref<number | undefined>(undefined)

const first = computed(() => props.points[0])
const last = computed(() => props.points.at(-1))
const fractions = computed(() => trendFractions(props.points))

const ticks = computed(() => trendTicks(props.points, 5))
const axisMin = computed(() => ticks.value[0])
const axisMax = computed(() => ticks.value.at(-1))

const padLeft = computed(() => {
  const longest = Math.max(
    0,
    ...ticks.value.map((value) => formatMoney(value, { locale: localeCode.value }).length),
  )
  return Math.max(MIN_PAD_LEFT, Math.ceil(longest * LABEL_CHAR_WIDTH) + 16)
})
const plotWidth = computed(() => VIEW_WIDTH - padLeft.value - PAD_RIGHT)

function chartX(fraction: number): number {
  return padLeft.value + fraction * plotWidth.value
}

function chartY(value: Money): number {
  const min = axisMin.value
  const max = axisMax.value
  if (min === undefined || max === undefined || min.units === max.units) return PAD_TOP + PLOT_HEIGHT / 2
  return PAD_TOP + PLOT_HEIGHT - moneyProportion(value, min, max) * PLOT_HEIGHT
}

const zeroY = computed(() => {
  const point = props.points[0]
  return point === undefined ? undefined : chartY(zeroMoney(point.netWorth.currency))
})

const linePath = computed(() =>
  props.points
    .map((point, index) => {
      const x = chartX(fractions.value[index] ?? 0)
      return `${index === 0 ? 'M' : 'L'}${x},${chartY(point.netWorth)}`
    })
    .join(' '),
)

const singlePointMarker = computed(() => {
  const point = props.points.length === 1 ? props.points[0] : undefined
  return point === undefined ? undefined : { x: chartX(0), y: chartY(point.netWorth) }
})

const belowZero = computed(() => {
  const baseline = zeroY.value
  const min = axisMin.value
  const max = axisMax.value
  if (baseline === undefined || min === undefined || max === undefined) return undefined
  if (min.units >= 0n || max.units < 0n || baseline >= BOTTOM) return undefined
  return { y: baseline, height: BOTTOM - baseline }
})

const yLabels = computed(() =>
  ticks.value.map((value) => ({
    y: chartY(value),
    label: formatMoney(value, { locale: localeCode.value }),
    isZero: isZeroMoney(value),
  })),
)

const xLabels = computed(() => {
  const last = props.points.length - 1
  const indices = [
    ...new Set(X_TICK_FRACTIONS.map((fraction) => nearestTrendIndex(fractions.value, fraction))),
  ]
  return indices.flatMap((index) => {
    const point = props.points[index]
    if (point === undefined) return []
    const anchor = index === 0 ? 'start' : index === last ? 'end' : 'middle'
    return [
      {
        index,
        x: chartX(fractions.value[index] ?? 0),
        label: formatDate(point.date, localeCode.value),
        anchor,
      },
    ]
  })
})

const summary = computed(() => {
  const start = first.value
  const end = last.value
  if (start === undefined || end === undefined) return undefined
  return {
    from: formatDate(start.date, localeCode.value),
    to: formatDate(end.date, localeCode.value),
    fromValue: start.netWorth,
    toValue: end.netWorth,
  }
})
const summaryId = useId()

const resolutionLabel = computed(() =>
  props.resolutionDays <= 1
    ? t('reporting.netWorthChart.resolutionDaily')
    : t('reporting.netWorthChart.resolution', { days: props.resolutionDays }),
)

function onPointerMove(event: PointerEvent): void {
  const box = plot.value?.getBoundingClientRect()
  if (box === undefined || box.width === 0) return
  const offset = (event.clientX - box.left) / box.width
  const fraction = Math.min(1, Math.max(0, (offset * VIEW_WIDTH - padLeft.value) / plotWidth.value))
  hovered.value = nearestTrendIndex(fractions.value, fraction)
}

function onPointerLeave(event: PointerEvent): void {
  if (event.pointerType !== 'touch') hovered.value = undefined
}
function onDocumentPointerDown(event: PointerEvent): void {
  if (!plot.value?.contains(event.target as Node)) hovered.value = undefined
}
onMounted(() => document.addEventListener('pointerdown', onDocumentPointerDown))
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocumentPointerDown))

const hoveredPoint = computed(() => {
  const index = hovered.value
  return index === undefined ? undefined : props.points[index]
})

const hoverX = computed(() => {
  const index = hovered.value
  return index === undefined ? 0 : chartX(fractions.value[index] ?? 0)
})

const tooltipStyle = computed(() => {
  const box = plot.value?.getBoundingClientRect()
  const pixelX = box === undefined ? 0 : (hoverX.value / VIEW_WIDTH) * box.width
  const onRight = box !== undefined && pixelX > box.width / 2
  return {
    left: `${pixelX}px`,
    transform: onRight ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)',
  }
})
</script>

<template>
  <section
    class="flex flex-col gap-3 rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-[var(--space-block)]"
  >
    <div class="flex flex-wrap items-baseline justify-between gap-2">
      <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
        {{ t('reporting.netWorthChart.title') }}
      </h2>
      <span
        class="flex items-center gap-[var(--space-tight)] text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
      >
        <svg width="12" height="8" aria-hidden="true">
          <line x1="0" y1="4" x2="12" y2="4" stroke="var(--color-text)" stroke-width="2" />
        </svg>
        {{ t('reporting.netWorthTitle') }}
      </span>
    </div>

    <div
      ref="plot"
      class="relative"
      @pointerdown="onPointerMove"
      @pointermove="onPointerMove"
      @pointerleave="onPointerLeave"
    >
      <svg
        role="img"
        :aria-labelledby="summaryId"
        :viewBox="`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`"
        class="h-auto w-full"
      >
        <rect
          v-if="belowZero"
          :x="padLeft"
          :y="belowZero.y"
          :width="plotWidth"
          :height="belowZero.height"
          fill="var(--color-negative)"
          fill-opacity="0.07"
          data-region="below-zero"
        />
        <g v-for="tick in yLabels" :key="`y-${tick.y}`">
          <line
            :x1="padLeft"
            :x2="VIEW_WIDTH - PAD_RIGHT"
            :y1="tick.y"
            :y2="tick.y"
            :stroke="tick.isZero ? 'var(--color-text-muted)' : 'var(--color-border)'"
            :stroke-width="tick.isZero ? 1.5 : 1"
          />
          <text
            :x="padLeft - 8"
            :y="tick.y"
            text-anchor="end"
            dominant-baseline="middle"
            :fill="tick.isZero ? 'var(--color-text)' : 'var(--color-text-muted)'"
            font-size="13"
            data-axis="y"
          >
            {{ tick.label }}
          </text>
        </g>

        <path
          :d="linePath"
          fill="none"
          stroke="var(--color-text)"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          data-series="net-worth"
        />

        <circle
          v-if="singlePointMarker"
          :cx="singlePointMarker.x"
          :cy="singlePointMarker.y"
          r="4"
          fill="var(--color-text)"
          data-marker="net-worth"
        />

        <line
          v-if="hoveredPoint"
          :x1="hoverX"
          :x2="hoverX"
          :y1="PAD_TOP"
          :y2="BOTTOM"
          stroke="var(--color-text-muted)"
          stroke-width="1"
          data-cursor="hover"
        />

        <text
          v-for="tick in xLabels"
          :key="`x-${tick.index}`"
          :x="tick.x"
          :y="BOTTOM + 20"
          :text-anchor="tick.anchor"
          fill="var(--color-text-muted)"
          font-size="13"
          data-axis="x"
        >
          {{ tick.label }}
        </text>
      </svg>

      <div
        v-if="hoveredPoint"
        class="pointer-events-none absolute top-0 flex flex-col gap-1 rounded-[var(--radius-control)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 shadow-[var(--shadow-overlay)]"
        :style="tooltipStyle"
        data-testid="net-worth-tooltip"
        aria-hidden="true"
      >
        <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
          formatDate(hoveredPoint.date, localeCode)
        }}</span>
        <MoneyAmount :value="hoveredPoint.netWorth" size="medium" />
      </div>
    </div>

    <i18n-t
      v-if="summary"
      :id="summaryId"
      keypath="reporting.netWorthChart.summary"
      tag="p"
      class="sr-only"
      :values="{ from: summary.from, to: summary.to }"
    >
      <template #fromValue><MoneyAmount :value="summary.fromValue" /></template>
      <template #toValue><MoneyAmount :value="summary.toValue" /></template>
    </i18n-t>

    <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{ resolutionLabel }}</p>

    <details class="text-[length:var(--text-step-2)]">
      <summary
        class="cursor-pointer text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
      >
        {{ t('reporting.netWorthChart.showTable') }}
      </summary>
      <table class="mt-2 w-full">
        <caption class="sr-only">
          {{
            t('reporting.netWorthChart.tableCaption')
          }}
        </caption>
        <thead>
          <tr
            class="border-b border-[color:var(--color-border)] text-left text-[color:var(--color-text-muted)]"
          >
            <th scope="col" class="py-1 font-medium">{{ t('reporting.netWorthChart.tableDate') }}</th>
            <th scope="col" class="py-1 text-right font-medium">
              {{ t('reporting.netWorthChart.tableValue') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(point, index) in points"
            :key="index"
            class="border-b border-[color:var(--color-border)] last:border-0"
          >
            <th scope="row" class="py-1 text-left font-normal">{{ formatDate(point.date, localeCode) }}</th>
            <td class="py-1 text-right">
              <MoneyAmount :value="point.netWorth" />
            </td>
          </tr>
        </tbody>
      </table>
    </details>
  </section>
</template>
